import { Config } from '@bfa/next-app-config';
import { clientLogging } from '@bfa/nextjs-common/services';
import auth0, { Auth0Error } from 'auth0-js';
import { useEffect, useState } from 'react';

const useAuth0LoginSession = () => {
  const [uri, setUri] = useState('');
  let webAuth: auth0.WebAuth;

  try {
    webAuth = new auth0.WebAuth({
      domain: Config.public.misc?.auth?.domain,
      clientID: Config.public.misc?.auth?.clientId,
      scope: Config.public.misc?.auth?.scope,
      audience: Config.public.misc?.auth?.audience,
      responseType: 'code',
    });
  } catch (err) {
    clientLogging.logError({
      message: `Error to create auth0 instance: ${(err as Error).message}`,
      name: 'Auth0LoginSession',
    });
  }

  useEffect(() => {
    const { host, protocol } = window.location;

    setUri(`${protocol}//${host}/api/auth/login`);
  }, []);

  const login = (
    email: string,
    password: string,
    config?: { returnTo: string }
  ): Promise<[unknown, Auth0Error | null | Error]> => {
    if (!webAuth) return Promise.resolve([null, Error('Missing config')]);

    // https://community.auth0.com/t/logout-redirect-url-ignores-additional-url-query-parameters-in-custom-rule/73219
    const encodedReturnTo =
      config?.returnTo && encodeURIComponent(config.returnTo);

    return new Promise((resolve) => {
      webAuth?.login(
        {
          password: password,
          email: email,
          realm: Config.public.misc.auth.realm,
          redirectUri: `${uri}${
            encodedReturnTo ? `?returnTo=${encodedReturnTo}` : ''
          }`,
          onRedirecting: function (done) {
            clientLogging.logInfo({
              content: '[Auth0] silence login',
              breadcrumbs: {
                email,
              },
            });
            done();
          },
        },
        function (err, result) {
          if (err) {
            clientLogging.logError({
              message: `Error during cross login: ${err.description}`,
              name: 'Auth0LoginSession',
            });

            return resolve([null, err]);
          }
          return resolve([result, null]);
        }
      );
    });
  };

  return {
    login,
  };
};

export default useAuth0LoginSession;
